/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";

import {
  ComplianceDocumentStatus,
  CompliancePayeeType,
  CompliancePayeeTypePayload,
  ComplianceTaxpayerType,
  ComplianceTaxpayerTypePayload,
  DocumentType,
  myAccountApiMessages,
  PayMethod,
  PayMethodMinimumAmount,
} from "~/features/myAccount/constants";
import MyAccountApi from "~/features/myAccount/MyAccountApi";
import transformToComplianceCountries from "~/features/myAccount/transformers/transformToComplianceCountries";
import transformToComplianceDetails from "~/features/myAccount/transformers/transformToComplianceDetails";
import transformToComplianceDocumentsById, {
  transformToComplianceDocumentsWithStatus as transformToComplianceDocumentsWithStatuses,
} from "~/features/myAccount/transformers/transformToComplianceDocuments";
import transformToComplianceReview from "~/features/myAccount/transformers/transformToComplianceReview";
import transformToComplianceStatus from "~/features/myAccount/transformers/transformToComplianceStatus";
import transformToCountries from "~/features/myAccount/transformers/transformToCountries";
import {
  PostComplianceDetailsPayload,
  PostFinalizeComplianceRequestBody,
  PostPaymentInfoPayload,
  PostSaveResellerInfoPayload,
  PostUpdateComplianceRequestBody,
  SaveResellerInfoPayload,
  UploadComplianceDocumentErrorResponse,
  UploadComplianceDocumentPayload,
} from "~/features/myAccount/types";
import isLatestReviewAvailable from "~/features/myAccount/utils/isLatestReviewAvailable";
import convertToBase64 from "~/utils/convertToBase64";
import createThunkError from "~/utils/createThunkError";
import getAsyncThunkError from "~/utils/errors/getAsyncThunkError";

export const initMyDetailsAsync = createAsyncThunk("myAccount/initMyDetails", async () => {
  const response = await MyAccountApi.getResellerDetails();

  return {
    email: response.data.result.email,
    phone: response.data.result.phone,
  };
});

export const saveMyDetailsAsync = createAsyncThunk(
  "myAccount/saveMyDetails",
  async (data: SaveResellerInfoPayload, thunkAPI) => {
    try {
      const payload: PostSaveResellerInfoPayload = {
        email: data.email,
        phone: data.phone,
        password: data.password.trim(),
      };

      if (data.new_password) {
        payload.newPassword = data.new_password.trim();
      }

      if (data.verify_new_password) {
        payload.newPasswordVerify = data.verify_new_password.trim();
      }

      await MyAccountApi.saveResellerInfo(payload);

      return data;
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        return thunkAPI.rejectWithValue(getAsyncThunkError(error, myAccountApiMessages));
      }

      throw error;
    }
  },
);

export const initPayoutInfoAsync = createAsyncThunk("myAccount/initPayoutInfo", async () => {
  const paymentInfoPromise = MyAccountApi.getPaymentInfo();
  const worldPayPromise = MyAccountApi.getWorldPayFieldMappings();
  const statusPromise = MyAccountApi.getComplianceStatus();

  const [paymentInfoRes, worldPayRes, statusRes] = await Promise.all([
    paymentInfoPromise,
    worldPayPromise,
    statusPromise,
  ]);

  const paymentInfoResData = paymentInfoRes.data.data;

  const submitted = !!paymentInfoResData.submitted;
  const minimumAmount = paymentInfoResData.minimum_amount ? Number(paymentInfoResData.minimum_amount) / 100 : 0;

  const paymethodInfo = paymentInfoRes.data.paymethodInfo;
  const paymethodInfoMinimum = paymethodInfo ? paymethodInfo[PayMethod.CHECK]?.minPayoutDollarCents : null;
  const paymethodMinimum = paymethodInfoMinimum ? paymethodInfoMinimum / 100 : PayMethodMinimumAmount[PayMethod.CHECK];

  const payoutData = {
    ...paymentInfoResData,
    minimum_amount_dollars: submitted && minimumAmount > 0 ? minimumAmount : paymethodMinimum, // Cents
  };

  return {
    payoutData: payoutData,
    countries: transformToCountries(paymentInfoRes.data, worldPayRes.data),
    restrictedCountries: paymentInfoRes.data.restrictedCountries,
    paymethodInfo: paymentInfoRes.data.paymethodInfo,
    missingData: paymentInfoRes.data.missingData,
    complianceStatus: transformToComplianceStatus(statusRes.data),
  };
});

export const savePayoutInfoAsync = createAsyncThunk(
  "myAccount/savePayoutInfoAsync",
  async (data: { payload: PostPaymentInfoPayload; isCreate: boolean }, thunkAPI) => {
    try {
      return data.isCreate
        ? await MyAccountApi.postPaymentInfoCreate(data.payload)
        : await MyAccountApi.postPaymentInfoUpdate(data.payload);
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const message = error.response?.data?.error as string;
        if (message) {
          return thunkAPI.rejectWithValue(createThunkError({ message: message }));
        }
      }

      throw error;
    }
  },
);

export const initDocumentsAsync = createAsyncThunk("myAccount/initDocuments", async () => {
  const statusPromise = MyAccountApi.getComplianceStatus();
  const reviewPromise = MyAccountApi.getComplianceReview();
  const documentsPromise = MyAccountApi.getComplianceDocuments();
  const complianceDetailsPromise = MyAccountApi.getComplianceDetails();

  const [statusRes, reviewRes, documentsRes, complianceRes] = await Promise.all([
    statusPromise,
    reviewPromise,
    documentsPromise,
    complianceDetailsPromise,
  ]);

  const reviewData = isLatestReviewAvailable(reviewRes.data) ? reviewRes.data : null;

  const pendingDocumentsById = transformToComplianceDocumentsById(documentsRes.data || []);
  const pendingDocumentsIds = Object.keys(pendingDocumentsById).map(Number);

  const returnData = {
    pendingDocuments: {
      byId: pendingDocumentsById,
      ids: pendingDocumentsIds,
    },
    complianceStatus: transformToComplianceStatus(statusRes.data),
    reviewData,
    payoutInfo: {
      resellerType: complianceRes.data.result.details.payeeType,
    },
    reviewDocuments: {
      byId: {},
      ids: [],
    },
  };

  if (reviewData) {
    const reviewInfoRes = await MyAccountApi.getComplianceReviewInfo(reviewData.id);

    const reviewDocumentsById = transformToComplianceDocumentsById(reviewInfoRes.data?.ReviewDocs || []);
    const reviewDocumentsIds = Object.keys(reviewDocumentsById).map(Number);

    returnData.reviewDocuments.byId = reviewDocumentsById;

    (returnData.reviewDocuments.ids as number[]) = reviewDocumentsIds;
  }

  return returnData;
});

export const uploadDocumentAsync = createAsyncThunk(
  "myAccount/uploadDocument",
  async (data: UploadComplianceDocumentPayload, thunkAPI) => {
    try {
      const file = data.uploads[0];
      const fileData = await convertToBase64(data.uploads[0]);
      const documentType = DocumentType[data.documenttype as keyof typeof DocumentType];
      await MyAccountApi.postUploadDocument(fileData, file.name, documentType, data.resellernote);

      const documentsRes = await MyAccountApi.getComplianceDocuments();
      const pendingDocumentsById = transformToComplianceDocumentsById(documentsRes.data || []);
      const pendingDocumentsIds = Object.keys(pendingDocumentsById).map(Number);

      return {
        byId: pendingDocumentsById,
        ids: pendingDocumentsIds,
      };
    } catch (error) {
      if (isAxiosError(error)) {
        const response = error.response?.data as UploadComplianceDocumentErrorResponse;
        const message = response.error;
        const maxLimitReached = response.result.maxLimitReached;

        if (maxLimitReached) {
          return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode: 400, message, data: maxLimitReached }));
        }
      }

      return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode: 500, message: "unknown error" }));
    }
  },
);

export const uploadDocumentAndRefreshDocumentsAsync = createAsyncThunk(
  "myAccount/uploadDocumentAndRefreshDocuments",
  async (data: UploadComplianceDocumentPayload, thunkAPI) => {
    try {
      const file = data.uploads[0];
      const fileData = await convertToBase64(data.uploads[0]);
      const documentType = DocumentType[data.documenttype as keyof typeof DocumentType];
      await MyAccountApi.postUploadDocument(fileData, file.name, documentType, data.resellernote);

      const complianceDetails = await MyAccountApi.getComplianceDetails();
      const { docs } = complianceDetails.data.result;
      const documentsWithStatus = transformToComplianceDocumentsWithStatuses(docs);

      return {
        pending: documentsWithStatus.filter((doc) => doc.status === ComplianceDocumentStatus.PENDING),
        submitted: documentsWithStatus.filter((doc) => doc.status === ComplianceDocumentStatus.SUBMITTED),
      };
    } catch (error) {
      if (isAxiosError(error)) {
        const response = error.response?.data as UploadComplianceDocumentErrorResponse;
        const message = response.error;
        const maxLimitReached = response.result.maxLimitReached;

        if (maxLimitReached) {
          return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode: 400, message, data: maxLimitReached }));
        }
      }

      return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode: 500, message: "unknown error" }));
    }
  },
);

export const deleteDocumentAsync = createAsyncThunk("myAccount/deleteDocument", async (id: number) => {
  await MyAccountApi.postDeleteDocument({
    documentid: id,
  });
});

export const deleteNewComplianceFormDocumentAsync = createAsyncThunk(
  "myAccount/deleteNewComplianceFormDocument",
  async (id: number) => {
    await MyAccountApi.postDeleteDocument({
      documentid: id,
    });
  },
);

export const startReviewAsync = createAsyncThunk("myAccount/startReview", async (_, thunkAPI) => {
  await MyAccountApi.postStartReview();

  await thunkAPI.dispatch(initDocumentsAsync());
});

export const initComplianceAsync = createAsyncThunk("myAccount/initCompliance", async () => {
  const complianceDetailsPromise = MyAccountApi.getComplianceDetails();
  const locationsPromise = MyAccountApi.getValidLocations();

  const [complianceDetails, validLocations] = await Promise.all([complianceDetailsPromise, locationsPromise]);
  const documents = transformToComplianceDocumentsWithStatuses(complianceDetails.data.result.docs);

  return {
    details: transformToComplianceDetails(complianceDetails.data.result.details),
    documents: {
      pending: documents.filter((doc) => doc.status === ComplianceDocumentStatus.PENDING),
      submitted: documents.filter((doc) => doc.status === ComplianceDocumentStatus.SUBMITTED),
    },
    review: transformToComplianceReview(complianceDetails.data.result.details),
    complianceDetails: complianceDetails.data,
    countries: transformToComplianceCountries(validLocations.data.result),
  };
});

export const createComplianceAsync = createAsyncThunk(
  "myAccount/createCompliance",
  async (data: { payload: PostComplianceDetailsPayload; finalize: boolean }, thunkAPI) => {
    try {
      const creationResponse = await MyAccountApi.postComplianceDetails(data.payload);
      const resellerReview = await thunkAPI.dispatch(initComplianceAsync()).unwrap();

      if (data.finalize) {
        const finalizeResponse = await MyAccountApi.postFinalizeComplianceReview({
          reviewId: resellerReview.review.reviewId,
        });
        await thunkAPI.dispatch(initComplianceAsync());

        return finalizeResponse;
      }

      return creationResponse;
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const message = error.response?.data?.error as string;

        if (message) {
          return thunkAPI.rejectWithValue(createThunkError({ message: message }));
        }
      }

      throw error;
    }
  },
);

export const updateComplianceAsync = createAsyncThunk(
  "myAccount/updateCompliance",
  async (data: { body: PostUpdateComplianceRequestBody; finalize: boolean }, thunkAPI) => {
    try {
      // TODO: Sorry for this part, it's just a quick fix for the release. During the CLEANUP, we should refactor it
      //  so that CompliancePayeeType and ComplianceTaxpayerType don't have string values. This way, the select field
      //  won't receive a string, and we won't need to transform it here or in transformToCompliancePayload.
      const transformedBody = {
        ...data.body,
        ...(data.body.payeeType && {
          payeeType: CompliancePayeeTypePayload[data.body.payeeType as unknown as CompliancePayeeType],
        }),
        ...(data.body.taxpayerType && {
          taxpayerType: ComplianceTaxpayerTypePayload[data.body.taxpayerType as unknown as ComplianceTaxpayerType],
        }),
      };

      const updateResponse = await MyAccountApi.postUpdateComplianceReview(transformedBody);
      const resellerReview = await thunkAPI.dispatch(initComplianceAsync()).unwrap();

      if (data.finalize) {
        const finalizeResponse = await MyAccountApi.postFinalizeComplianceReview({
          reviewId: resellerReview.review.reviewId,
        });
        await thunkAPI.dispatch(initComplianceAsync());

        return finalizeResponse;
      }

      return updateResponse;
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const message = error.response?.data?.error as string;

        if (message) {
          return thunkAPI.rejectWithValue(createThunkError({ message: message }));
        }
      }

      throw error;
    }
  },
);

export const finalizeComplianceAsync = createAsyncThunk(
  "myAccount/finalizeCompliance",
  async (data: { body: PostFinalizeComplianceRequestBody }, thunkAPI) => {
    try {
      const finalizeResponse = await MyAccountApi.postFinalizeComplianceReview(data.body);
      await thunkAPI.dispatch(initComplianceAsync());

      return finalizeResponse;
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const message = error.response?.data?.error as string;

        if (message) {
          return thunkAPI.rejectWithValue(createThunkError({ message: message }));
        }
      }

      throw error;
    }
  },
);
